// Third-party
import React from 'react';
import { connect } from 'react-redux';
import FocusTrap from 'focus-trap-react';

// Components
import { QuestionnaireViewContainer, RiskProfiles } from '../../questionnaire';
import Modal from '@/views/components/modals/AccessibleModal';

// Actions
import { ReduxState } from '@/redux/modules/types';
import {
  getUnansweredKycQuestions,
  calculateKycQuestionnaireResult,
  completeKycQuestionnaire,
  getKycSectionSubtotal,
  goBackKycQuestionnaire,
  saveKycOption,
  selectRiskProfile,
  setRiskProfile,
  setRiskProfileScore,
} from '@/redux/modules/kyc-questionnaire';

// Utils
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { formatMessage } from '@/utils/i18n';
import Questionnaire from '@/types/questionnaire';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionItem from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import RiskProfileItem from '@/types/risk-profile-item';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import { UpRiskWarningModal } from './components/UpRiskWarningModal';
import { validateUpRisk } from './utils/validate-uprisk';
import SkipActionBox from './components/SkipActionBox';
import { castKycQuestionnaire } from '@/utils/risk-profiles';

type StateProps = {
  questionnaire?: Questionnaire;
  user?: Nullable<User>;
  locale: Locale;
  isCompleted: boolean;
  recommendedRiskProfile?: RiskProfileItem;
};

type DispatchProps = {
  getUnansweredKycQuestions: (section: QuestionnaireSectionItem) => QuestionItem[];
  calculateKycQuestionnaireResult: () => void;
  completeKycQuestionnaire: () => void;
  getKycSectionSubtotal: (section: QuestionnaireSectionItem) => number;
  goBackKycQuestionnaire: () => void;
  saveKycOption: (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string) => void;
  selectRiskProfile: (profile: RiskProfileItem) => void;
  setRiskProfileScore: (score?: number) => void;
  setRiskProfile: () => void;
};

type KycQuestionnaireProps = {
  newRiskProfile: Partial<ContactRiskProfile>;
  riskProfiles: RiskProfileItem[];
  minRiskCalculation?: boolean;
  scoreCalculation?: boolean;
  title?: string;
};

type Props = StateProps &
  DispatchProps &
  KycQuestionnaireProps & {
    isModal: boolean;
    show?: boolean;
    current?: {
      contact: GenericContact;
    };
    saveLabel?: string;
    onClose?: () => void;
    onSave: () => void;
    onClosed?: () => void;
    isSaving?: boolean;
  };

const CreateKycQuestionnaire: React.FC<Props> = ({
  questionnaire,
  user,
  locale,
  isCompleted,
  recommendedRiskProfile,
  getUnansweredKycQuestions,
  calculateKycQuestionnaireResult,
  completeKycQuestionnaire,
  getKycSectionSubtotal,
  goBackKycQuestionnaire,
  saveKycOption,
  selectRiskProfile,
  setRiskProfile,
  setRiskProfileScore,
  newRiskProfile,
  riskProfiles,
  minRiskCalculation,
  scoreCalculation,
  title,
  isModal,
  show = false,
  current,
  saveLabel,
  onClose,
  onSave,
  onClosed,
  isSaving,
}) => {
  const [showWarning, setShowWarning] = React.useState(false);
  const showWarningModal = showWarning && (!isModal || show);

  const focusTrapOptions = React.useMemo(
    () => ({
      preventScroll: true,
      checkCanFocusTrap: (trapContainers: Array<HTMLElement | SVGElement>): Promise<any> => {
        const results = trapContainers.map(
          trapContainer =>
            new Promise<void>(resolve => {
              const interval = setInterval(() => {
                if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                  resolve();
                  clearInterval(interval);
                }
              }, 5);
            }),
        );

        return Promise.all(results);
      },
    }),
    [],
  );

  if (!questionnaire) {
    return <></>;
  }

  const sectionIsDisabled = (section: QuestionnaireSectionItem) => {
    const sortedSections = [...questionnaire.sections].sort((a, b) => a.order - b.order);
    const previousSections = sortedSections.slice(
      0,
      sortedSections.findIndex(s => s._id === section._id),
    );
    return previousSections.some(s => getUnansweredKycQuestions(s).length > 0);
  };

  const setQuestionnaireComplete = () => {
    calculateKycQuestionnaireResult();
    completeKycQuestionnaire();
  };

  const checkUpRiskAndSave = () => {
    const { profile } = newRiskProfile;

    const isUpRisk = validateUpRisk(profile, recommendedRiskProfile, user);

    if (!isUpRisk) {
      onSave();
      return;
    }

    setShowWarning(true);
  };

  const getQuestionnaireProps = () => {
    const canViewQuestionnaire = !!user && userHasPermission(user, PERMISSIONS.RISK_QUESTIONNAIRE) && questionnaire.sections.length > 0;

    return {
      title: title ?? formatMessage('CreateRiskProfile.NewRiskProfile'),
      isCompleted,
      userPermissionType: PERMISSIONS.RATIONALE_QUESTIONNAIRE,
      newRiskProfile,
      riskProfiles,
      questionnaire,
      minRiskCalculation: !!minRiskCalculation,
      user,
      current,
      onSave: checkUpRiskAndSave,
      onClose: () => onClose && onClose(),
      saveLabel,
      sectionIsDisabled,
      setQuestionnaireComplete,
      getUnansweredQuestions: getUnansweredKycQuestions,
      getSectionSubtotal: getKycSectionSubtotal,
      goBackQuestionnaire: goBackKycQuestionnaire,
      saveAnswers: saveKycOption,
      savedSections: newRiskProfile?.savedSections ? newRiskProfile.savedSections : {},
      disableSave: !newRiskProfile.profile,
      customQuestionnaireView:
        isCompleted || !canViewQuestionnaire ? (
          <RiskProfiles
            locale={locale}
            user={user}
            newRiskProfile={newRiskProfile}
            recommendedRiskProfile={recommendedRiskProfile}
            riskProfiles={riskProfiles}
            setRiskProfileScore={setRiskProfileScore}
            setRiskProfile={setRiskProfile}
            selectRiskProfile={selectRiskProfile}
            minRiskCalculation={minRiskCalculation}
            scoreCalculation={scoreCalculation}
            onBack={questionnaire && questionnaire.sections.length > 0 ? goBackKycQuestionnaire : undefined}
          />
        ) : undefined,
      customQuestionnaireViewHeader: <SkipActionBox contact={current?.contact} completeKycQuestionnaire={completeKycQuestionnaire} />,
      isSaving,
    };
  };

  if (questionnaire === undefined) {
    return <></>;
  }

  return (
    <>
      <UpRiskWarningModal show={showWarningModal} onConfirm={onSave} onCancel={() => setShowWarning(false)} />

      {isModal ? (
        <Modal
          show={show && !showWarning}
          backdropClassName="modal-v2-backdrop"
          className="full-screen-md-modal risk-profile-modal"
          onHide={onClose}
          onExited={onClosed}
          dialogClassName="modal-dialog-centered"
          backdrop="static"
          autoFocus={false}
          enforceFocus={false}
        >
          <FocusTrap focusTrapOptions={focusTrapOptions}>
            <Modal.Body className="d-flex flex-column">
              <QuestionnaireViewContainer {...getQuestionnaireProps()} />
            </Modal.Body>
          </FocusTrap>
        </Modal>
      ) : (
        <QuestionnaireViewContainer {...getQuestionnaireProps()} />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  locale: state.intl.locale,
  isCompleted: state.kycQuestionnaire.isCompleted,
  recommendedRiskProfile: state.kycQuestionnaire.recommendedRiskProfile,
  questionnaire: state.kycQuestionnaire.currentQuestionnaire ? castKycQuestionnaire(state.kycQuestionnaire.currentQuestionnaire) : undefined,
});

export default connect(mapStateToProps, {
  getUnansweredKycQuestions,
  calculateKycQuestionnaireResult,
  completeKycQuestionnaire,
  getKycSectionSubtotal,
  goBackKycQuestionnaire,
  saveKycOption,
  selectRiskProfile,
  setRiskProfile,
  setRiskProfileScore,
})(CreateKycQuestionnaire);
